import { useState, useRef, useEffect } from 'react'
import { Send, Loader2, Zap, Globe, FileCheck, Github, ExternalLink, Rocket, CheckCircle, XCircle, PartyPopper, Link2, Package } from 'lucide-react'
import { useStore } from '@store/useStore'
import { createOrchestrator, PHASES, PHASE_INFO } from '@agents/orchestrator'
import { notifyGenerationComplete, notifyError } from '@services/notifications'
import { isConfigured as isGithubConfigured, deploySite } from '@services/github'
import { exportAndDownload } from '@utils/exportZip'
// Note: debug mode is tracked in the store (ui.debugMode) for reactive updates
import Message from './Message'
import AgentMessage from './AgentMessage'
import DebugContextPanel from './DebugContextPanel'

// Pipeline states
const PIPELINE_STATE = {
  IDLE: 'idle',           // No pipeline started
  PREPARING: 'preparing', // Running 5 prep agents
  READY: 'ready',         // Prep done, waiting for user validation
  GENERATING: 'generating', // Running Content + Images
  DONE: 'done',           // All done
}

export default function ChatPanel({ initialDomain, onDomainConsumed }) {
  const [input, setInput] = useState('')
  const [pipelineState, setPipelineState] = useState(PIPELINE_STATE.IDLE)
  const [deployStatus, setDeployStatus] = useState(null) // null | 'deploying' | 'done' | 'error'
  const [deployResult, setDeployResult] = useState(null)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const orchestratorRef = useRef(null)
  const hasAutoStarted = useRef(false)

  const {
    messages,
    addMessage,
    updateAgentMessage,
    project,
    setDomain,
    setPhase,
    setContext,
    addFile,
    setLoading,
    setImages,
    setLinkPlacements,
    ui,
    settings,
    progress,
    setProgress,
    setSummary,
  } = useStore()

  const isLoading = ui.isLoading

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input when not loading
  useEffect(() => {
    if (!isLoading) inputRef.current?.focus()
  }, [isLoading])

  // DEBUG: Log pipeline state changes
  useEffect(() => {
    console.log('🎯 [DEBUG] Pipeline state changed:', pipelineState, '| isLoading:', isLoading)
    console.log('🎯 [DEBUG] Button should show:', pipelineState === PIPELINE_STATE.READY && !isLoading)
  }, [pipelineState, isLoading])

  // Welcome message (only if no auto-start domain)
  useEffect(() => {
    if (messages.length === 0 && !initialDomain) {
      addMessage({
        role: 'assistant',
        content: `Salut ! Je suis **EMD Factory**.

Entre un nom de domaine EMD (ex: \`plombier-paris-15.fr\`) et je génère ton site complet.

**Comment ça marche :**

1. **Phase Préparation** (5 agents IA)
   - Stratège → Analyse SERP et positionnement
   - Identité → Persona et ton éditorial
   - Architecte → Structure et navigation
   - Rédacteur → Briefs SEO par page
   - DA → Charte graphique

2. **Tu valides** les documents générés

3. **Phase Génération** (2 agents IA)
   - Content → HTML/CSS complet
   - Images → Visuels générés par IA

**Balance ton NDD !**`,
      })
    }
  }, [])

  // Auto-start pipeline if domain passed from onboarding
  useEffect(() => {
    if (initialDomain && !hasAutoStarted.current && pipelineState === PIPELINE_STATE.IDLE) {
      hasAutoStarted.current = true
      // Add user message for the domain
      addMessage({ role: 'user', content: initialDomain })
      // Start the pipeline
      startPreparation(initialDomain)
      // Notify parent that domain has been consumed
      onDomainConsumed?.()
    }
  }, [initialDomain, pipelineState])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return

    const userInput = input.trim()
    setInput('')

    // Add user message
    addMessage({ role: 'user', content: userInput })

    // Check if it looks like a domain
    const domainPattern = /^[a-z0-9-]+\.[a-z]{2,}$/i
    const isDomain = domainPattern.test(userInput) || userInput.includes('.')

    if (isDomain && !project.domain) {
      // Start preparation phase
      await startPreparation(userInput)
    } else {
      // General response
      addMessage({
        role: 'assistant',
        content: `Pour démarrer, entre un nom de domaine comme \`plombier-paris-15.fr\`.

Je lancerai ensuite les 5 agents de préparation.`,
      })
    }
  }

  // Start PREPARATION phase (5 agents)
  const startPreparation = async (domain) => {
    setLoading(true)
    setDomain(domain)
    setPipelineState(PIPELINE_STATE.PREPARING)

    addMessage({
      role: 'assistant',
      content: `Démarrage de la **phase préparation** pour **${domain}**...

5 agents IA vont analyser ton projet et créer les documents fondateurs.`,
      type: 'info',
    })

    try {
      const orchestrator = createOrchestrator({
        provider: settings.provider,
        model: settings.model,
        // Dual-model configuration
        useDualModel: settings.useDualModel || false,
        reasoningModel: settings.reasoningModel || 'sonnet',
        productionModel: settings.productionModel || 'opus',
        // Cross-provider mode (anti-fingerprint)
        reasoningProvider: settings.reasoningProvider || null,
        productionProvider: settings.productionProvider || null,
        cmsPattern: settings.cmsPattern || localStorage.getItem('emd_cms_pattern') || 'tailwind',

        onPhaseChange: (phase) => {
          setPhase(phase)
          const info = PHASE_INFO[phase]
          // Only add agent message for actual agent phases (not init/done)
          if (info && phase !== PHASES.INIT && phase !== PHASES.DONE) {
            setProgress({ phase, status: 'running', message: info.description })
            // Add agent message with "running" status immediately
            addMessage({
              role: 'agent',
              agent: phase,
              summary: null, // Will be updated when complete
              status: 'running',
              metrics: [],
            })
          }
        },

        onProgress: ({ phase, status, message }) => {
          setProgress({ phase, status, message })
        },

        onMessage: (msg) => {
          addMessage(msg)
        },

        onFileGenerated: (name, content) => {
          console.log('📄 [CHATPANEL] File generated:', name)
          addFile(name, content)
          // Auto-switch to Pages tab when first file arrives
          if (Object.keys(useStore.getState().files).length === 0) {
            useStore.getState().setRightPanel('pages')
          }
        },

        onError: (error) => {
          addMessage({
            role: 'assistant',
            content: `Erreur: ${error.message}`,
            type: 'error',
          })
        },

        onSummary: (phase, summary) => {
          // Store AI-generated summary for nudge marketing display
          console.log(`📊 [CHATPANEL] Received summary for ${phase}:`, summary?.headline)
          setSummary(phase, summary)

          // Update existing agent message with completed status and summary
          if (summary?.headline) {
            updateAgentMessage(phase, {
              summary: summary.headline,
              status: 'complete',
              metrics: summary.metrics || [],
            })
          }
        },

        // Deep Debug: Show raw context in conversation (authorized users only)
        onDebugContext: (debugInfo) => {
          console.log('🔐 [DEEP DEBUG] Context:', debugInfo.title)
          addMessage({
            role: 'debug',
            debug: debugInfo,
          })
        },
      })

      // Store orchestrator for later use in generation phase
      orchestratorRef.current = orchestrator

      const result = await orchestrator.runPreparation(domain)

      // DEBUG: Log the result to understand what's happening
      console.log('🔍 [DEBUG] runPreparation result:', {
        success: result.success,
        error: result.error,
        phase: result.phase,
        hasContext: !!result.context,
        contextKeys: result.context ? Object.keys(result.context) : [],
      })

      if (result.success) {
        // Store context in global store
        setContext(result.context)

        // DEBUG: Log architecte structure IN DETAIL
        console.log('🔍 [DEBUG] Architecte context:', {
          hasArchitecte: !!result.context?.architecte,
          hasArchitecture: !!result.context?.architecte?.architecture,
          architecteKeys: result.context?.architecte ? Object.keys(result.context.architecte) : [],
          architectureKeys: result.context?.architecte?.architecture ? Object.keys(result.context.architecte.architecture) : [],
          pageCount: result.context?.architecte?.pageCount,
        })

        // Get pages - architecte agent now exposes pages at top level after bulletproof extraction
        const arch = result.context?.architecte
        const pages = arch?.pages || arch?.architecture?.pages || []
        const pageCount = pages.length

        console.log('🔍 [DEBUG] Pages extracted:', { pageCount, firstPage: pages[0]?.name })

        // DEBUG: Log pages
        console.log('🔍 [DEBUG] Pages found:', {
          count: pageCount,
          firstPage: pages[0],
          allPages: pages,
        })

        // Stop here and wait for user validation
        setPipelineState(PIPELINE_STATE.READY)
        console.log('🔍 [DEBUG] Pipeline state set to READY')

        // Switch to Pages tab to show the blueprint
        useStore.getState().setRightPanel('pages')

        addMessage({
          role: 'assistant',
          content: `**Préparation terminée !**

**${pageCount} pages** définies :
${pages.slice(0, 5).map(p => `- ${p.title || p.slug}`).join('\n')}${pages.length > 5 ? `\n- ... et ${pages.length - 5} autres` : ''}

Clique sur **"Générer le site"** quand tu es prêt !`,
          type: 'success',
        })
      } else {
        // DEBUG: Log failure
        console.error('🔍 [DEBUG] Preparation FAILED:', result.error)
        setPipelineState(PIPELINE_STATE.IDLE)
        const isCreditsError = result.error?.toLowerCase().includes('crédit')
        addMessage({
          role: 'assistant',
          content: isCreditsError
            ? `**Crédits insuffisants !**

${result.error}

Va dans **Paramètres** et utilise un code promo comme \`DEVMODE\` ou \`REFILL50\` pour recharger.`
            : `La préparation a échoué: ${result.error}

Vérifie tes clés API et réessaie.`,
          type: 'error',
        })
      }
    } catch (error) {
      setPipelineState(PIPELINE_STATE.IDLE)
      addMessage({
        role: 'assistant',
        content: `Une erreur s'est produite: ${error.message}

Vérifie tes clés API dans les paramètres.`,
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  // Handle generation phase (after user validates)
  const handleGeneration = async () => {
    if (!orchestratorRef.current) {
      addMessage({
        role: 'assistant',
        content: 'Erreur: pas de préparation en cours. Relance avec un domaine.',
        type: 'error',
      })
      return
    }

    setLoading(true)
    setPipelineState(PIPELINE_STATE.GENERATING)

    // Switch to Pipeline tab to show progress
    useStore.getState().setRightPanel('pipeline')

    addMessage({
      role: 'assistant',
      content: 'Génération du site en cours...',
      type: 'info',
    })

    try {
      const genResult = await orchestratorRef.current.runGeneration()

      if (genResult.success) {
        setPipelineState(PIPELINE_STATE.DONE)

        const pageCount = Object.keys(genResult.files).length
        const imageCount = genResult.images?.length || 0

        // Store generated images in global store for export
        if (genResult.images?.length > 0) {
          setImages(genResult.images)
          console.log('🖼️ [CHATPANEL] Stored', genResult.images.length, 'images in global store')
        }

        // Store link placements in global store for UI display
        if (genResult.linkPlacements) {
          setLinkPlacements(genResult.linkPlacements)
          console.log('🔗 [CHATPANEL] Stored link placements:', genResult.linkPlacements)
        }

        // Send browser notification
        notifyGenerationComplete(project.domain, pageCount)

        // Switch to Pages tab to show results
        useStore.getState().setRightPanel('pages')

        // Build enhanced summary message
        const linkInfo = genResult.linkPlacements
        const stats = genResult.stats
        let summaryParts = [
          `**Site généré !**`,
          ``,
          `- **${pageCount}** pages HTML`,
          `- **${imageCount}** images${imageCount > 0 ? ' (incluses dans l\'export)' : ''}`,
        ]

        // Add link placement info
        if (linkInfo?.moneySite) {
          summaryParts.push(`- 💰 **Money site** sur \`${linkInfo.moneySite.page}\``)
        }
        if (linkInfo?.resourceLinks?.length > 0) {
          const resourcePages = [...new Set(linkInfo.resourceLinks.flatMap(rl => rl.pages || []))]
          summaryParts.push(`- 🔗 **${linkInfo.resourceLinks.length} liens ressource** répartis sur ${resourcePages.length} page(s)`)
        }

        // Add cost info
        if (stats?.totalCost) {
          summaryParts.push(``)
          summaryParts.push(`**Coût total:** ${stats.totalCost}`)
        }

        summaryParts.push(``)
        summaryParts.push(`Prévisualise dans le panneau de droite ou exporte en ZIP.`)

        addMessage({
          role: 'assistant',
          content: summaryParts.join('\n'),
          type: 'celebration',
        })

        // Auto-download ZIP after generation completes
        console.log('📦 [CHATPANEL] Auto-downloading ZIP...')
        try {
          await exportAndDownload({
            files: genResult.files,
            images: genResult.images || [],
            domain: project.domain,
          })
          console.log('📦 [CHATPANEL] ZIP downloaded successfully')
        } catch (zipError) {
          console.error('📦 [CHATPANEL] Auto-download failed:', zipError)
          // Silent fail - user can still use manual ZIP button
        }
      } else {
        setPipelineState(PIPELINE_STATE.READY)
        notifyError(`Génération échouée: ${genResult.error}`)
        addMessage({
          role: 'assistant',
          content: `Génération échouée: ${genResult.error}`,
          type: 'error',
        })
      }
    } catch (error) {
      setPipelineState(PIPELINE_STATE.READY)
      addMessage({
        role: 'assistant',
        content: `Erreur de génération: ${error.message}`,
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) =>
          msg.role === 'agent' ? (
            <AgentMessage
              key={msg.id}
              agent={msg.agent}
              summary={msg.summary}
              status={msg.status}
              metrics={msg.metrics}
            />
          ) : msg.role === 'debug' ? (
            // Only show debug panels when debug mode is enabled in store
            ui.debugMode && <DebugContextPanel key={msg.id} debug={msg.debug} />
          ) : (
            <Message key={msg.id} message={msg} />
          )
        )}

        {/* Inline Generate button - shown in conversation flow */}
        {pipelineState === PIPELINE_STATE.READY && !isLoading && (
          <div className="flex gap-3 max-w-3xl animate-in fade-in slide-in-from-bottom-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-violet-500 flex items-center justify-center flex-shrink-0 shadow-lg">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 space-y-3">
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl rounded-tl-sm p-4">
                <p className="text-sm text-slate-300 mb-3">
                  Consulte les pages dans le panneau de droite avant de lancer la génération.
                </p>
                <button
                  onClick={handleGeneration}
                  className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-primary-600 to-violet-600 hover:from-primary-500 hover:to-violet-500 rounded-xl font-bold shadow-lg shadow-primary-500/25 transition-all transform hover:scale-[1.02]"
                >
                  <Rocket className="w-5 h-5" />
                  Générer le site
                </button>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>


      {/* Site generated - show export options */}
      {pipelineState === PIPELINE_STATE.DONE && !isLoading && (
        <div className="px-4 pb-4 space-y-3">
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
            <FileCheck className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-green-400 font-medium">Site généré avec succès !</p>
            <p className="text-xs text-slate-400 mt-1">
              Prévisualise ou exporte ton site depuis le panneau de droite.
            </p>
          </div>

          {/* Deploy to GitHub */}
          {deployStatus === 'done' && deployResult ? (
            <div className="bg-primary-500/10 border border-primary-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                  <Github className="w-5 h-5 text-primary-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Déployé sur GitHub Pages</p>
                  <a
                    href={deployResult.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1"
                  >
                    {deployResult.url}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          ) : isGithubConfigured() ? (
            <button
              onClick={async () => {
                setDeployStatus('deploying')
                try {
                  const repoName = project.domain.replace(/\./g, '-').toLowerCase()
                  const result = await deploySite({
                    repoName,
                    files,
                    onProgress: ({ message }) => {
                      addMessage({ role: 'assistant', content: message, type: 'info' })
                    },
                  })
                  setDeployResult(result)
                  setDeployStatus('done')
                  addMessage({
                    role: 'assistant',
                    content: `**Site déployé !**\n\nURL: ${result.url}\nRepo: ${result.repo}`,
                    type: 'success',
                  })
                } catch (error) {
                  setDeployStatus('error')
                  addMessage({
                    role: 'assistant',
                    content: `Erreur de déploiement: ${error.message}`,
                    type: 'error',
                  })
                }
              }}
              disabled={deployStatus === 'deploying'}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 border border-slate-700 rounded-xl transition-all"
            >
              {deployStatus === 'deploying' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Déploiement en cours...</span>
                </>
              ) : (
                <>
                  <Rocket className="w-5 h-5 text-primary-400" />
                  <span>Déployer sur GitHub Pages</span>
                </>
              )}
            </button>
          ) : (
            <div className="text-center py-2">
              <p className="text-xs text-slate-500">
                Connecte GitHub dans les paramètres pour déployer automatiquement
              </p>
            </div>
          )}
        </div>
      )}

      {/* Input - shown when idle, ready, or done (not while actively running) */}
      {(pipelineState === PIPELINE_STATE.IDLE || pipelineState === PIPELINE_STATE.READY || pipelineState === PIPELINE_STATE.DONE) && (
        <form onSubmit={handleSubmit} className="p-4 border-t border-slate-800">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  pipelineState === PIPELINE_STATE.DONE
                    ? 'Entre un autre domaine pour un nouveau site'
                    : pipelineState === PIPELINE_STATE.READY
                    ? 'Pose une question ou génère le site...'
                    : 'Entre un nom de domaine (ex: plombier-paris-15.fr)'
                }
                disabled={isLoading}
                className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500 disabled:opacity-50"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="px-4 py-3 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
